import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import ppeExecutionService from "../../services/ppeExecutionService";
import OperationReleaseDrawer from "./OperationReleaseDrawer";

export default function ExecutionOperationList() {

  const { planId } = useParams();
  const navigate = useNavigate();

  const [operations, setOperations] = useState([]);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------------------
  // Load operations
  // ------------------------------------------------------------------
  const loadOperations = async () => {
    setLoading(true);
    try {
      const data =
        await ppeExecutionService.listPlanOperations(planId);

      setOperations(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOperations();
  }, [planId]);

  // ------------------------------------------------------------------
  // Helpers (SAFE FIELD RESOLUTION)
  // ------------------------------------------------------------------
  const getStatus = (row) =>
    row.status || row.status_code || row.status_label || "PLANNED";

  const getOperationName = (row) =>
    row.operation_name ||
    row.operation_label ||
    row.process_name ||
    row.operation_code ||
    row.routing_operation_name ||
    "—";

  // ------------------------------------------------------------------
  // Progress
  // ------------------------------------------------------------------
  const totalOps = operations.length;

  const releasedOps =
    operations.filter(o =>
      ["RELEASED", "IN_PROGRESS", "COMPLETED"]
        .includes(getStatus(o))
    ).length;

  const progress =
    totalOps === 0 ? 0 :
      Math.round((releasedOps / totalOps) * 100);

  const getBadge = (status) => {
    switch (status) {
      case "PLANNED": return "secondary";
      case "RELEASED": return "primary";
      case "IN_PROGRESS": return "warning";
      case "COMPLETED": return "success";
      default: return "secondary";
    }
  };

  const isReady = (row) => {
    const prev = operations
      .filter(o => o.operation_seq_no < row.operation_seq_no)
      .sort((a, b) => b.operation_seq_no - a.operation_seq_no)[0];

    if (!prev) return true;

    return ["RELEASED", "IN_PROGRESS", "COMPLETED"]
      .includes(getStatus(prev));
  };

  // ------------------------------------------------------------------
  // UI
  // ------------------------------------------------------------------
  return (
    <div>

      {/* HEADER */}
      <div
        style={{
          background: "linear-gradient(90deg,#0ea5a4,#5eead4)",
          color: "#fff",
          padding: "18px 24px",
          borderRadius: 10,
          marginBottom: 10
        }}
      >
        <h2 style={{ margin: 0 }}>
          Production Execution — Plan #{planId}
        </h2>
        <div style={{ opacity: 0.9 }}>
          Release operations to shop floor
        </div>
      </div>

      {/* BACK BUTTON */}
      <div style={{ marginBottom: 12 }}>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => navigate("/ppe/execution")}
        >
          ← Back to Execution Dashboard
        </button>
      </div>

      {/* PROGRESS */}
      <div className="mb-3">
        <strong>Progress:</strong>
        <div className="progress mt-1" style={{ height: 18 }}>
          <div
            className="progress-bar bg-success"
            style={{ width: `${progress}%` }}
          >
            {progress}%
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th width="80">Seq</th>
                <th>Operation</th>
                <th width="160">Status</th>
                <th width="160">Action</th>
              </tr>
            </thead>

            <tbody>

              {loading && (
                <tr>
                  <td colSpan="4" className="text-center py-4">
                    Loading operations...
                  </td>
                </tr>
              )}

              {!loading && operations.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center py-4">
                    No operations found
                  </td>
                </tr>
              )}

              {operations.map(row => {

                const status = getStatus(row);
                const ready = isReady(row);

                return (
                  <tr key={row.plan_operation_id}>
                    <td>{row.operation_seq_no}</td>

                    {/* ✅ FIXED OPERATION NAME */}
                    <td>{getOperationName(row)}</td>

                    <td>
                      <span
                        className={`badge bg-${getBadge(status)}`}
                      >
                        {status}
                      </span>

                      {!ready && status === "PLANNED" && (
                        <span className="ms-2 text-danger">
                          ● BLOCKED
                        </span>
                      )}
                    </td>

                    <td>
                      {/* ✅ FIXED RELEASE BUTTON LOGIC */}
                      {status === "PLANNED" && ready ? (
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => setSelectedOperation(row)}
                        >
                          Release
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}

            </tbody>
          </table>
        </div>
      </div>

      {/* DRAWER */}
      {selectedOperation && (
        <OperationReleaseDrawer
          operation={selectedOperation}
          planId={planId}
          onClose={() => {
            setSelectedOperation(null);
            loadOperations();
          }}
        />
      )}

    </div>
  );
}